// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useQuery } from '@tanstack/react-query';
import { listArtifacts, type ListArtifactsParams } from '../services/sopFactoryService';
import type { PagedArtifacts } from '../types/procedures';
import { shouldRetry } from '../../helios/config/queryClient';

// The router's `status` filter has no "all statuses" sentinel (see
// sopFactoryService), so getting every artifact means one call per known
// status.
export const ALL_STATUSES = ['draft', 'in_review', 'approved', 'deprecated'] as const;
export const FANOUT_PAGE_SIZE = 200;

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  deprecated: 'Deprecated',
};
const labelOf = (status: string) => STATUS_LABELS[status] ?? status;
const listOf = (statuses: string[]) => statuses.map(labelOf).join(', ');

export interface StatusFanout {
  /** The page per status that loaded. A failed status has no entry. */
  byStatus: Partial<Record<string, PagedArtifacts>>;
  /** Statuses whose request failed; their artifacts are missing from `byStatus`. */
  failedStatuses: string[];
  /**
   * Statuses whose page came back full. There is no paging over this fan-out,
   * so anything past `FANOUT_PAGE_SIZE` for that status is not shown.
   */
  truncatedStatuses: string[];
}

/** True when the upstream says, or the page size implies, that more exist. */
export function isTruncated(page: PagedArtifacts, pageSize = FANOUT_PAGE_SIZE): boolean {
  return page.total != null ? page.total > page.items.length : page.items.length >= pageSize;
}

/**
 * `allSettled` means a single failed status never rejects the queryFn, so
 * react-query's own retry never fires for it. One in-line retry under the same
 * policy (`shouldRetry`) keeps a one-off 502 from being stored as a partial
 * catalog until someone clicks Retry.
 */
async function listWithRetry(params: ListArtifactsParams): Promise<PagedArtifacts> {
  try {
    return await listArtifacts(params);
  } catch (err) {
    if (!shouldRetry(0, err)) throw err;
    return listArtifacts(params);
  }
}

/**
 * One `listArtifacts` call per status, settled independently: a status that
 * fails drops out and is named, instead of failing the whole catalog. Rejects
 * only when every status failed — then there is nothing to show.
 */
export async function fetchStatusPages(
  statuses: readonly string[],
  params: Omit<ListArtifactsParams, 'status' | 'page_size'> = {}
): Promise<StatusFanout> {
  const settled = await Promise.allSettled(
    statuses.map(status => listWithRetry({ ...params, status, page_size: FANOUT_PAGE_SIZE }))
  );
  const fanout: StatusFanout = { byStatus: {}, failedStatuses: [], truncatedStatuses: [] };
  settled.forEach((result, i) => {
    const status = statuses[i];
    if (result.status === 'rejected') {
      fanout.failedStatuses.push(status);
      return;
    }
    fanout.byStatus[status] = result.value;
    if (isTruncated(result.value)) fanout.truncatedStatuses.push(status);
  });
  if (fanout.failedStatuses.length === statuses.length) {
    throw (settled[0] as PromiseRejectedResult).reason;
  }
  return fanout;
}

/** Every loaded artifact, in status order. */
export const fanoutItems = (fanout: StatusFanout | undefined) =>
  fanout ? Object.values(fanout.byStatus).flatMap(page => page?.items ?? []) : [];

/**
 * Operator-facing sentence for a fan-out that is partial or capped, or null
 * when it is complete. One place for the wording so Home and Library agree.
 */
export function fanoutNotice(fanout: StatusFanout | undefined): string | null {
  if (!fanout) return null;
  const parts: string[] = [];
  if (fanout.failedStatuses.length > 0) {
    parts.push(
      `Couldn't load ${listOf(fanout.failedStatuses)} artifacts; the other statuses are shown.`
    );
  }
  if (fanout.truncatedStatuses.length > 0) {
    parts.push(
      `Only the first ${FANOUT_PAGE_SIZE} ${listOf(fanout.truncatedStatuses)} artifacts are shown.`
    );
  }
  return parts.length > 0 ? parts.join(' ') : null;
}

const QUERY_KEY = ['admin-center', 'procedures', 'all-statuses'] as const;

/**
 * Fetches every status's artifact page under one react-query cache key, so
 * Home's `useProcedures` (stats) and Library's `useProcedureLibrary`
 * ("All statuses" view) share this fan-out instead of each re-running the
 * same 4 requests under separate keys.
 */
export function useAllStatusArtifacts(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: QUERY_KEY,
    enabled: options.enabled ?? true,
    queryFn: () => fetchStatusPages(ALL_STATUSES),
  });
}

export type AllStatusArtifactsResult = StatusFanout;
